import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/dashboard`;

// Mirrors BlackBox.Application.Manage.DashboardSummaryDto.
export interface DashboardTotals {
  total: number;
  succeeded: number;
  warning: number;
  failed: number;
}

export interface DashboardWorkflowEntry {
  workflowId: string;
  name: string;
  total: number;
  succeeded: number;
  warning: number;
  failed: number;
}

export interface DashboardGroupEntry {
  key: string;
  label: string;
  total: number;
  succeeded: number;
  warning: number;
  failed: number;
  errorCount: number;
}

export interface DashboardUsageBucket {
  start: string;
  label: string;
}

export interface DashboardUsageSeries {
  name: string;
  data: number[];
}

export interface DashboardUsage {
  granularity: "hourly" | "daily" | "weekly" | "monthly";
  buckets: DashboardUsageBucket[];
  series: DashboardUsageSeries[];
}

export interface DashboardSummary {
  hasData: boolean;
  totals: DashboardTotals;
  workflows: DashboardWorkflowEntry[];
  /** Which dimension the Errors panel groups by: "product" | "project" | "workflow". */
  groupDimension: string;
  groups: DashboardGroupEntry[];
  usage: DashboardUsage;
}

const EMPTY_SUMMARY: DashboardSummary = {
  hasData: false,
  totals: { total: 0, succeeded: 0, warning: 0, failed: 0 },
  workflows: [],
  groupDimension: "workflow",
  groups: [],
  usage: { granularity: "daily", buckets: [], series: [] },
};

// READ — org-scoped run-history roll-up powering the home dashboard panels.
// range: "auto" (default — server picks the time-bucket granularity from the data span),
// "24h" | "7d" | "30d" | "90d".
export function useDashboardSummary(range: Ref<string>) {
  const { data, isFetching, error } = useQuery({
    queryKey: computed(() => ["dashboard-summary", range.value]),
    queryFn: () => fetchWrapper.get<DashboardSummary>(`${BASE}/run-summary?range=${range.value}`),
  });
  const summary = computed(() => data.value ?? EMPTY_SUMMARY);

  return {
    summary,
    summaryLoading: isFetching,
    summaryError: error as Ref<ApiError | null>,
  };
}
